import { Button, Grid2, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import { CommonPagination, GKTable } from "components";
import {
  ConstituentScreeningIcon,
  DeleteIcon,
  ReloadIcon,
} from "components/Icons";
import { useState } from "react";
import toast from "react-hot-toast";
import { CgMathPlus } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, moneyCommaSeparatorPoint } from "utils/helpers";
import CreateAdvisorModal from "./CreateAdvisorModal";

const AdvisorTable = () => {
  const navigate = useNavigate();

  const {
    state: { commonFilter },
  } = useAppContext();

  const [advisorCreateModal, setAdvisorCreateModal] = useState(false);

  const requestBody = {
    requireTotalCount: true,
    take: commonFilter?.advisor?.take,
    skip: commonFilter?.advisor?.skip,
    sort: JSON.stringify(
      commonFilter?.advisor?.sortModel?.map((data: any) => {
        return {
          selector: data.field,
          desc: data.sort !== "asc",
        };
      })
    ),
    search: commonFilter?.advisor?.search || "",
  };

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["DASHBOARD_ADVISOR_TABLE", requestBody],
    queryFn: () =>
      axiosInstance.get(`/super_admin/advisors/`, {
        params: requestBody,
      }),
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const { mutate: refreshAccount } = useMutation({
    mutationKey: ["REFRESH_ACCOUNT"],
    mutationFn: (id: number | string) =>
      axiosInstance.get(`/super_admin/${id}/refresh_account/`),
    onSuccess: (response) => {
      toast.success(response?.data?.message || "Refresh Account Successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const { mutate: deleteClient } = useMutation({
    mutationKey: ["DELETE_CLIENT"],
    mutationFn: (data) =>
      axiosInstance.delete(`/super_admin/${data}/delete-advisor/`),
    onSuccess: () => {
      refetch();
    },
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Company Name", "companyName", 200, 1),
      valueGetter: (row) => row?.row?.companyName || "-",
    },
    {
      ...extraColumnsField("Advisor Name", "name", 170, 1),
      valueGetter: (row) => row?.row?.name || "-",
    },
    {
      ...extraColumnsField("Phone Number", "phone", 150, 1),
      type: "number",
      valueGetter: (row) => row?.row?.phone || "-",
    },
    {
      ...extraColumnsField("Total Aum", "totalAum", 130, 1),
      type: "number",
      valueGetter: (row) => row?.row?.totalAum || 0,
      valueFormatter: (params) => moneyCommaSeparatorPoint(params.value),
    },
    {
      ...extraColumnsField("Clients", "clients", 90, 1),
      type: "number",
      valueGetter: (row) => row?.row?.clients || 0,
    },
    {
      ...extraColumnsField("Portfolios", "portfolios", 100, 1),
      type: "number",
      valueGetter: (row) => row?.row?.portfolios || 0,
    },
    {
      ...extraColumnsField("Created on", "createdOn", 120, 1),
      // valueFormatter: (params) => dayjs(params?.value).format("DD MMM YYYY"),
      valueGetter: (row) => row?.row?.createdOn || "N/A",
    },
    {
      ...extraColumnsField("Actions", "action", 150, 1),
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <Grid2 container>
            <Tooltip arrow placement="top" title="Refresh the account">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  refreshAccount(params.id);
                }}
              >
                <ReloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip arrow placement="top" title="View Details">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/app/advisor/${params.row.id}/details`);
                }}
              >
                <ConstituentScreeningIcon />
              </IconButton>
            </Tooltip>
            <Tooltip arrow placement="top" title="Delete Advisor">
              <IconButton
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  deleteClient(params?.row?.id);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Grid2>
        );
      },
    },
    // {
    //   headerName: "Delete Client",
    //   flex: 1,
    //   field: "deleteClient",
    //   align: "right",
    //   headerAlign: "right",
    //   renderCell: (item) => (
    //     <IconButton
    //       color="error"
    //       onClick={(e) => {
    //         e.stopPropagation();
    //         e.preventDefault();
    //         deleteClient(item?.row?.id);
    //       }}
    //     >
    //       <MdDelete />
    //     </IconButton>
    //   ),
    // },
  ];

  const cusPagination = () => {
    return (
      <CommonPagination
        name="advisor"
        totalCount={data?.data.totalCount || 0}
      />
    );
  };

  return (
    <>
      <GKTable
        loading={isFetching}
        columns={columns}
        rows={data?.data?.data || []}
        name="advisor"
        CustomPagination={!isFetching && cusPagination}
        onRowClick={(params) => navigate(`/app/preference/${params.row.phone}`)}
        headerComponent={
          <Button
            onClick={() => setAdvisorCreateModal(true)}
            variant="contained"
            size="medium"
          >
            <CgMathPlus size={18} /> Create Advisor
          </Button>
        }
      />

      {advisorCreateModal && (
        <CreateAdvisorModal
          open={advisorCreateModal}
          setOpen={setAdvisorCreateModal}
          refetch={refetch}
        />
      )}
    </>
  );
};

export default AdvisorTable;
